import React, { useEffect, useRef, useState } from "react";
import { Alert, Container, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { authFetch } from "../utils/authFetch";
import { useAuth } from "../auth/AuthContext";
import useDocumentClosing from "../../hooks/useDocumentClosing";
import ViewNotaModal from "../ViewNotaModal";
import HelpModal from "./HelpModal";
import { useHelpHotkey } from "../../hooks/useHelpHotkey";
import DetaliiScazutModal from "../bon_consum/DetaliiScazutModal";

export default function OperationalDocumentEditor({ create = false, kind, FormComponent, HelpComponent }) {
  const config = {
    cash: { api: '/cash-doc', path: '/cash', title: 'Plată / Încasare', permission: 'CASH_VALIDATE' },
    transfer: { api: '/bon_transfer', path: '/bon-transfer', title: 'Bon de transfer', recType: 4, permission: 'BON_VALIDATE' },
    consumption: { api: '/bon-consum', path: '/bon-consum', title: 'Bon de consum', recType: 3, permission: 'BON_VALIDATE' },
  }[kind];
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const closingEnabled = useDocumentClosing();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(!create);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [generation, setGeneration] = useState(0);
  const [showNota, setShowNota] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const { showHelp, setShowHelp } = useHelpHotkey();
  const inFlight = useRef(false);
  const locked = !!(doc?.bxfinal_bool || doc?.is_closed);
  useEffect(() => {
    if (create) return;
    let alive = true;
    setLoading(true);
    authFetch(`${config.api}/${id}`).then(data => { if (alive) setDoc(data); })
      .catch(e => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [create, id, config.api]);

  async function save(payload) {
    if (inFlight.current || locked) return;
    inFlight.current = true; setBusy(true); setError('');
    try {
      const body = config.recType ? { ...payload, rec_type: config.recType } : payload;
      const result = await authFetch(`${config.api}/${create ? '' : id}`, {
        method: create ? 'POST' : 'PUT', body: JSON.stringify(body),
      });
      const savedId = result?.id || result?.doc?.id || (!create && id);
      if (!savedId) throw new Error('Salvarea nu a returnat documentul. Verificați lista înainte de a reîncerca.');
      toast.success('Documentul a fost salvat.');
      if (create) nav(`${config.path}/${savedId}`, { replace: true });
      else { setDoc(result?.id ? result : await authFetch(`${config.api}/${savedId}`)); setGeneration(n => n + 1); }
      return result;
    } catch (e) { setError(e.message); throw e; }
    finally { inFlight.current = false; setBusy(false); }
  }

  async function changeState(finalize) {
    if (inFlight.current || !window.confirm(finalize ? 'Finalizezi documentul? Nu va mai putea fi modificat.' : 'Redeschizi documentul pentru modificări?')) return;
    inFlight.current = true; setBusy(true); setError('');
    try {
      const action = kind === 'cash' ? (finalize ? 'close' : 'reopen') : (finalize ? 'finalize' : 'unfinalize');
      await authFetch(`${config.api}/${id}/${action}`, { method: 'POST' });
      setDoc(await authFetch(`${config.api}/${id}`)); setGeneration(n => n + 1);
      toast.success(finalize ? 'Document finalizat.' : 'Document redeschis.');
    } catch (e) { setError(e.message); }
    finally { inFlight.current = false; setBusy(false); }
  }

  if (loading) return <Container className="py-3"><Spinner size="sm" /> Se încarcă documentul…</Container>;
  if (!create && !doc) return <Container className="py-3"><Alert variant="danger">{error || 'Document inexistent.'}</Alert><button className="btn btn-outline-secondary" onClick={() => nav(config.path)}>Înapoi</button></Container>;
  const actions = [
    !create && { label: 'Vezi nota contabilă', onClick: () => setShowNota(true) },
    !create && kind !== 'cash' && { label: 'Tipărește', onClick: () => window.open(`${config.path}/${id}/print`, '_blank') },
    !create && kind === 'consumption' && { label: 'Detaliu scădere', onClick: () => setShowDetails(true) },
    HelpComponent && { label: 'Ajutor (F1)', onClick: () => setShowHelp(true) },
  ];
  return <Container className="py-3">
    <FormComponent key={`${create ? 'new' : id}:${generation}`} initialDoc={doc} onSubmit={save} busy={busy}
      isClosed={locked} recType={config.recType} documentActions={{
        title: `${create ? 'Creează ' : ''}${config.title}${create ? '' : ` #${id}`}`,
        newDocument: create, error, onBack: () => nav(config.path),
        onAdd: !create ? () => nav(`${config.path}/${kind === 'transfer' ? 'create' : 'new'}`) : undefined,
        addLabel: 'Adaugă document', closingEnabled,
        onFinalize: !create ? () => changeState(true) : undefined,
        canReopen: kind === 'cash' || user?.is_superuser || user?.perms?.includes(config.permission),
        onReopen: () => changeState(false), actions,
      }} />
    {!create && <ViewNotaModal recType={config.recType || doc.rec_type} docId={Number(id)} show={showNota} onHide={() => setShowNota(false)} />}
    {kind === 'consumption' && !create && <DetaliiScazutModal bonId={id} show={showDetails} onHide={() => setShowDetails(false)} />}
    {HelpComponent && <HelpModal show={showHelp} onHide={() => setShowHelp(false)} title={`Ajutor — ${config.title}`}><HelpComponent /></HelpModal>}
  </Container>;
}
